import React, { useState } from 'react';
import { SimpleSwitch } from '@/components/ui';
import { UserGroupService } from '@/services/userGroups.service';
import { useNotificationStore } from '@/store/notificationStore';
import { UserGroupRightsDialog } from './UserGroupRightsDialog';
import type { GroupRole, UserGroup } from '@/types/userGroups.types';

interface MobileRoleCardProps {
  groupRole: GroupRole;
  userGroups: UserGroup[];
  onUpdateUserGroupRoles: (roleName: string) => void;
}

/**
 * MobileRoleCard - Card layout for a single role on mobile.
 * Shows the role name as a header, then lists each user group with its toggle below.
 */
export const MobileRoleCard: React.FC<MobileRoleCardProps> = ({
  groupRole,
  userGroups,
  onUpdateUserGroupRoles,
}) => {
  const { addNotification } = useNotificationStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdvancedEditOpen, setIsAdvancedEditOpen] = useState(false);
  const [localRoleStates, setLocalRoleStates] = useState<Record<number, boolean>>({});

  if (!groupRole || !userGroups || !Array.isArray(userGroups)) return null;

  const hasRole = (userGroup: UserGroup): boolean => {
    if (!userGroup || !groupRole) return false;
    if (Object.prototype.hasOwnProperty.call(localRoleStates, userGroup.id)) {
      return localRoleStates[userGroup.id];
    }
    if (!userGroup.roleCodes) return false;
    return userGroup.roleCodes.includes(groupRole.roleCode);
  };

  const isRequiredRole = (userGroup: UserGroup): boolean => {
    if (!userGroup || !userGroup.requiredRoleCodes || !groupRole) return false;
    return userGroup.requiredRoleCodes.includes(groupRole.roleCode);
  };

  const handleToggleRole = async (userGroup: UserGroup, newValue: boolean) => {
    setLocalRoleStates((prev) => ({ ...prev, [userGroup.id]: newValue }));
    try {
      await UserGroupService.updateGroupRoleStatus(groupRole.roleCode, userGroup.id, newValue);
    } catch (error) {
      setLocalRoleStates((prev) => ({ ...prev, [userGroup.id]: !newValue }));
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification({
        type: 'error',
        title: 'Role Update Failed',
        message: errorMessage,
      });
    }
  };

  const handleAdvancedEdit = () => {
    setIsMenuOpen(false);
    setIsAdvancedEditOpen(true);
  };

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-600 dark:bg-dark-900">
        {/* Card Header — role name + actions menu */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-dark-700 dark:bg-dark-800">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {groupRole.roleName}
          </span>
          <div className="relative">
            <button
              onClick={() => setIsMenuOpen((v) => !v)}
              className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-dark-700 dark:hover:text-gray-300"
              title="More actions"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
            {isMenuOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setIsMenuOpen(false)} />
                <div className="absolute right-0 top-full z-30 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg dark:border-dark-600 dark:bg-dark-700">
                  <div className="py-1">
                    <button
                      onClick={handleAdvancedEdit}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-600"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Advanced Edit
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Group rows */}
        <div className="divide-y divide-gray-100 dark:divide-dark-700">
          {userGroups.filter(Boolean).map((userGroup) => {
            if (!userGroup || !userGroup.id) return null;

            const isActive = hasRole(userGroup);
            const isRequired = isRequiredRole(userGroup);
            const isGroupInactive = !userGroup.isActive;

            return (
              <div key={userGroup.id} className="flex items-center justify-between px-4 py-2.5">
                <span
                  className={
                    isGroupInactive
                      ? 'text-sm text-gray-400 dark:text-gray-500'
                      : 'text-sm text-gray-700 dark:text-gray-300'
                  }
                >
                  {userGroup.name}
                  {isGroupInactive && (
                    <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-400 dark:bg-dark-700 dark:text-gray-500">
                      Inactive
                    </span>
                  )}
                </span>

                {isRequired ? (
                  <div
                    className="flex h-6 w-11 items-center justify-center rounded-full bg-gray-200 dark:bg-dark-600"
                    title={`Required for ${userGroup.name}`}
                  >
                    <svg
                      className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                ) : isGroupInactive ? (
                  <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                ) : (
                  <SimpleSwitch
                    checked={isActive}
                    onChange={(checked) => handleToggleRole(userGroup, checked)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <UserGroupRightsDialog
        isOpen={isAdvancedEditOpen}
        onClose={() => setIsAdvancedEditOpen(false)}
        groupRole={groupRole}
        userGroups={userGroups}
        onSuccess={() => onUpdateUserGroupRoles(groupRole.roleName)}
      />
    </>
  );
};
